import { useAtom } from "jotai";
import { memo, useCallback, useMemo } from "react";
import Col from "react-bootstrap/Col";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Row from "react-bootstrap/Row";
import Tooltip from "react-bootstrap/Tooltip";
import { animated, useTransition } from "react-spring";
import { pushGTMAnalyticsEvent } from "../../../modules/pushGTMAnalyticsEvent";
import { items_state, products_state, update_items_activeIds } from "../../dataManagers/GlobalDataManagers";
import { Boop } from "../Animations/Boop";
import { ItemsHeader } from "../ItemsSection/ItemsHeader";
import useFilterColor from "../ItemsSection/useFilterColor";
import "./styles.scss";

export const SIDE_LOGO_COMPONENT_ID = "side-logo";
const NO_SIDE_LOGOS_ITEM = "no-side-logos";
const YES_SIDE_LOGOS_ITEM = "yes-side-logos";
const NO_TOP_LOGO_ITEM = "no-top-logo";
const DEFAULT_TOP_LOGO_ITEM = "white-color-logo";

/**
 * LogoToggleSection
 *
 *   • Top Logo  — togglable; colour swatches apply only when top is on.
 *   • Side Logos — togglable add-on; hardcoded mesh (no colour picker).
 *
 * At least one must be active. Clicking Top Logo while it's the only option
 * switches to Side Logos. Side Logos active → custom-patches-side excluded
 * via components.json dependency on no-side-logos.
 */
export const LogoToggleSection = memo(({ component, componentItems, children, ...props }) => {
  const [itemsState] = useAtom(items_state);
  const [productsState] = useAtom(products_state);
  const [, setItemsActiveIds] = useAtom(update_items_activeIds);

  const omitSidePatches = productsState.activeObj?.shopify?.["omit-side-patches"] === true;

  const topLogoItemId = itemsState.activeIds?.[component._id]?._id ?? DEFAULT_TOP_LOGO_ITEM;
  const topLogoActive = topLogoItemId !== NO_TOP_LOGO_ITEM;

  const sideLogosActive = itemsState.activeIds?.[SIDE_LOGO_COMPONENT_ID]?._id === YES_SIDE_LOGOS_ITEM;

  const canTurnOffTop = sideLogosActive && !omitSidePatches;

  const filteredColors = useFilterColor({ componentItems, component });

  const animationOnItems = useTransition(filteredColors, {
    trail: 40,
    initial: { opacity: 0, scale: 0 },
    enter: { opacity: 1, scale: 1 },
    update: { enter: { opacity: 1, scale: 1 } },
    reset: true,
  });

  const activeColorId = topLogoActive ? topLogoItemId : null;

  const handleTopLogoClick = () => {
    if (topLogoActive) {
      // Only Top Logo is on — switch over to Side Logos instead of no-op
      if (!canTurnOffTop) {
        if (omitSidePatches) return;

        const copy = { ...itemsState.activeIds };
        copy[component._id] = { _id: NO_TOP_LOGO_ITEM };
        copy[SIDE_LOGO_COMPONENT_ID] = { _id: YES_SIDE_LOGOS_ITEM };
        copy["custom-patches-side"] = { _id: "no-side-patches" };
        setItemsActiveIds(copy);

        pushGTMAnalyticsEvent({
          event: "Item Click - Thrill",
          page: component.category,
          itemSection: component._id,
          item: "top-logo-switched-to-side-logos",
        });
        return;
      }

      const copy = { ...itemsState.activeIds };
      copy[component._id] = { _id: NO_TOP_LOGO_ITEM };
      setItemsActiveIds(copy);

      pushGTMAnalyticsEvent({
        event: "Item Click - Thrill",
        page: component.category,
        itemSection: component._id,
        item: "top-logo-deselected",
      });
      return;
    }

    const copy = { ...itemsState.activeIds };
    copy[component._id] = { _id: DEFAULT_TOP_LOGO_ITEM };
    setItemsActiveIds(copy);

    pushGTMAnalyticsEvent({
      event: "Item Click - Thrill",
      page: component.category,
      itemSection: component._id,
      item: "top-logo-selected",
    });
  };

  const handleSideLogosClick = () => {
    const copy = { ...itemsState.activeIds };

    if (sideLogosActive) {
      copy[SIDE_LOGO_COMPONENT_ID] = { _id: NO_SIDE_LOGOS_ITEM };

      if (!topLogoActive) {
        copy[component._id] = { _id: DEFAULT_TOP_LOGO_ITEM };
      }
    } else {
      copy[SIDE_LOGO_COMPONENT_ID] = { _id: YES_SIDE_LOGOS_ITEM };
      copy["custom-patches-side"] = { _id: "no-side-patches" };
    }

    setItemsActiveIds(copy);

    pushGTMAnalyticsEvent({
      event: "Item Click - Thrill",
      page: component.category,
      itemSection: component._id,
      item: sideLogosActive ? "side-logos-deselected" : "side-logos-selected",
    });
  };

  const handleSwatchClick = useCallback(
    (itemId) => {
      if (!topLogoActive) return;
      if (itemsState.activeIds?.[component._id]?._id === itemId) return;

      const copy = { ...itemsState.activeIds };
      copy[component._id] = { _id: itemId };
      setItemsActiveIds(copy);

      pushGTMAnalyticsEvent({
        event: "Item Click - Thrill",
        page: component.category,
        itemSection: component._id,
        item: itemId,
      });
    },
    [topLogoActive, itemsState.activeIds, component._id, component.category],
  );

  const colNumber = useMemo(() => {
    if (component.items.length < 2) return 6;
    else if (component.items.length === 3) return 4;
    else return 3;
  }, [component.items.length]);

  const dataToDisplayTransitionArray = useMemo(() => animationOnItems, [component]);

  const activeColorName = topLogoActive ? itemsState.activeObjs?.[component._id]?.displayName : null;

  const items = dataToDisplayTransitionArray((style, item) => {
    const isActive = item._id === activeColorId;

    return (
      <Col key={item._id} xs={colNumber} className="my-2">
        <animated.div style={style}>
          <Boop boopType="scale" scale={1.02} timing={200}>
            {isActive && item._id !== "matching-color-stitching" && <div className="swatch-name">{item.displayName}</div>}
            <OverlayTrigger
              key={item._id}
              placement="bottom"
              overlay={
                <Tooltip id={`tooltip-details-${item._id}`} style={{ zIndex: 3000, transitionDelay: "200ms" }}>
                  {item.displayName}
                </Tooltip>
              }
            >
              <div className={`image-item ${isActive ? "activeImage" : ""}`} onClick={() => handleSwatchClick(item._id)}>
                {item.isNew && !isActive && (
                  <img
                    className={`new-icon ${["white-on-white-color-logo", "lavender-color-logo"].includes(item._id) ? "rectangular-positioning" : ""}`}
                    src="/images/new-icon-orange.png"
                    alt="new"
                  />
                )}
                <img
                  className={`imageItems position-relative ${component.category}`}
                  style={{
                    borderRadius: item?._id?.includes("color-logo") ? 0 : undefined,
                  }}
                  src={item.thumbnailSrc || item.imageSrc}
                  alt={item.displayName}
                />
              </div>
            </OverlayTrigger>
          </Boop>
        </animated.div>
      </Col>
    );
  });

  return (
    <div className="LogoToggleSection ItemsSection my-3">
      {!omitSidePatches && (
        <>
          <ItemsHeader component={component} componentItems={componentItems} {...props} />

          <p className="toggle-hint text-center mb-2">Select one or both:</p>

          <div className="logo-toggle-buttons d-flex justify-content-center gap-3 mb-3">
            <button
              type="button"
              className={`logo-toggle-btn ${topLogoActive ? "active-toggle" : "inactive-toggle"}`}
              onClick={handleTopLogoClick}
              aria-pressed={topLogoActive}
            >
              {topLogoActive && <span className="toggle-check">✓</span>} Top Logo
            </button>

            <button
              type="button"
              className={`logo-toggle-btn ${sideLogosActive ? "active-toggle" : "inactive-toggle"}`}
              onClick={handleSideLogosClick}
              aria-pressed={sideLogosActive}
            >
              {sideLogosActive && <span className="toggle-check">✓</span>} Side Logos
            </button>
          </div>
        </>
      )}

      {(omitSidePatches || topLogoActive) && activeColorName && (
        <div className="text-center mb-2">
          <h5 className="sectionHeader">Top Logo: {activeColorName}</h5>
        </div>
      )}

      {(omitSidePatches || topLogoActive) && (
        <Row className="items text-center justify-content-around">
          {items}
          {children}
        </Row>
      )}
    </div>
  );
});
